export type CampusLogoVariant = "main" | "black" | "stroke"

type CampusLike = {
  campusNo?: string | number
  name?: string
  schoolName?: string
  logoUrl?: string | null
  blackLogoUrl?: string | null
  strokeLogoUrl?: string | null
}

const STROKE_FALLBACK_CAMPUSES = new Set(["1", "3", "4"])

export const getCampusLogoFallbackUrl = (
  campusNo?: string | number,
  variant: CampusLogoVariant = "main"
) => {
  const no = String(campusNo || "").trim()
  if (!no) return ""
  if (variant === "stroke" && !STROKE_FALLBACK_CAMPUSES.has(no)) return ""
  const suffix = variant === "main" ? "" : `-${variant}`
  return `/assets/img/logos/campus-${no}${suffix}.png`
}

export const withCampusLogoFallbacks = <T extends CampusLike>(campus: T): T => ({
  ...campus,
  logoUrl: campus.logoUrl || getCampusLogoFallbackUrl(campus.campusNo, "main") || campus.logoUrl,
  blackLogoUrl: campus.blackLogoUrl || getCampusLogoFallbackUrl(campus.campusNo, "black") || campus.blackLogoUrl,
  strokeLogoUrl: campus.strokeLogoUrl || getCampusLogoFallbackUrl(campus.campusNo, "stroke") || campus.strokeLogoUrl,
})

export const campusBrandName = (campus?: CampusLike | null) =>
  campus?.schoolName || campus?.name || "School"

// For single-campus schools, the branch's own `name` is often just a repeat
// of the brand (e.g. Eon Academy has one branch also named "The Eon
// Academy") — printing both looks like a duplicated line. Multi-campus
// brands (e.g. Metropolitan Academy's Naseerabad/O-Level branches) have a
// genuinely distinct branch name worth showing. Only surface it when it
// actually differs from the brand name.
export const campusBranchLabel = (campus?: CampusLike | null) => {
  const brand = campusBrandName(campus).trim().toLowerCase()
  const name = (campus?.name || "").trim().toLowerCase()
  return name && name !== brand ? campus?.name || "" : ""
}

// Admission Letter: campus 1 & 2 use the standard Matric/AKU-EB letter;
// campus 3 & 4 (O-Level system) get a distinct O-Level letter;
// campus 6 (The Eon Academy) gets its dedicated admission letter;
// campus 5 does not have an admission letter yet, so the button remains hidden there.
const O_LEVEL_ADMISSION_CAMPUSES = new Set(["3", "4"])
const EON_ADMISSION_CAMPUSES = new Set(["6"])
const ADMISSION_LETTER_HIDDEN_CAMPUSES = new Set(["5"])

export const isOLevelAdmissionCampus = (campusNo?: string | number) =>
  O_LEVEL_ADMISSION_CAMPUSES.has(String(campusNo ?? "").trim())

export const isEonAdmissionCampus = (campusNo?: string | number) =>
  EON_ADMISSION_CAMPUSES.has(String(campusNo ?? "").trim())

export const isAdmissionLetterHiddenCampus = (campusNo?: string | number) =>
  ADMISSION_LETTER_HIDDEN_CAMPUSES.has(String(campusNo ?? "").trim())

export const absoluteCampusLogoUrl = (url?: string | null) => {
  const fallback = `${window.location.origin}/assets/img/icons/logo.png`
  if (!url) return fallback
  if (url.startsWith("/")) return `${window.location.origin}${url}`
  return url
}

